import React, { useMemo } from 'react';
import { cn } from '../../../lib/utils';
import { formatDateTime, formatDateTimeForList } from '../../../config/appConfig';
import { useDisplayTimeZone, useDisplayTimeZoneCaption, useEffectiveDisplayTimeZone } from '../../../hooks/useDisplayTimeZone';
import { parseEmailDateHeader } from '../utils/emailDateHeader';
import { buildEmailDateHintContent } from '../utils/emailDateHint';
import { EmailDateHintPopover } from './EmailDateHintPopover';

interface EmailDateDisplayProps {
  date: string;
  messageHeaders?: string | null;
  receivedDate?: string;
  /** List/header chip: single line */
  compact?: boolean;
  /** Timezone / header date details popover (message view only) */
  showHint?: boolean;
  className?: string;
  mutedClassName?: string;
}

export const EmailDateDisplay: React.FC<EmailDateDisplayProps> = ({
  date,
  messageHeaders,
  receivedDate,
  compact = false,
  showHint = true,
  className,
  mutedClassName,
}) => {
  const displayTimeZone = useDisplayTimeZone();
  const effectiveZone = useEffectiveDisplayTimeZone();
  const zoneCaption = useDisplayTimeZoneCaption();
  const formatOpts = useMemo(
    () => (displayTimeZone ? { timeZone: displayTimeZone } : undefined),
    [displayTimeZone],
  );
  const headerInfo = useMemo(
    () => (showHint ? parseEmailDateHeader(messageHeaders) : {}),
    [showHint, messageHeaders],
  );

  const formatted = compact
    ? formatDateTimeForList(date, formatOpts)
    : formatDateTime(date, formatOpts);

  const hintContent = useMemo(
    () =>
      showHint
        ? buildEmailDateHintContent({
            zoneCaption,
            effectiveZone,
            displayPreference: displayTimeZone,
            date,
            formatOpts,
            headerInfo,
            receivedDate,
          })
        : null,
    [showHint, zoneCaption, effectiveZone, displayTimeZone, date, formatOpts, headerInfo, receivedDate],
  );

  const content = (
    <span
      className={cn(
        'inline-flex items-center gap-0.5 min-w-0 max-w-full',
        compact ? className : mutedClassName,
      )}
    >
      <span className="whitespace-nowrap truncate">{formatted}</span>
      {showHint && hintContent && (
        <EmailDateHintPopover content={hintContent} primaryTime={formatted} compact={compact} />
      )}
    </span>
  );

  if (compact) {
    return content;
  }

  return (
    <div className={cn('text-right min-w-0', className)}>
      {content}
    </div>
  );
};
